import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  Edit as EditIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';

const RMStatusUpdate = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filters, setFilters] = useState({
    steel_code: '',
    location: '',
    status: ''
  });

  const [formData, setFormData] = useState({
    srr_code: '',
    current_location: '',
    status: '',
    available_quantity: '',
    reserved_quantity: '',
    remarks: ''
  });

  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { api } = useAuth();

  // Fetch RM Status data
  const { data: rmStatusData, isLoading, error, refetch } = useQuery(
    ['rmStatus', page, rowsPerPage, searchTerm, filters],
    async () => {
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
        ...filters
      });
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await api.get(`/raw-material/status?${params}`);
      return response.data;
    },
    {
      keepPreviousData: true,
      onError: (error) => {
        enqueueSnackbar(`Error fetching RM status: ${error.message}`, { variant: 'error' });
      }
    }
  );

  // Update mutation
  const updateMutation = useMutation(
    ({ srr_code, data }) => api.put(`/raw-material/status/${srr_code}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('rmStatus');
        enqueueSnackbar('RM status updated successfully', { variant: 'success' });
        handleCloseDialog();
      },
      onError: (error) => {
        enqueueSnackbar(`Error updating status: ${error.response?.data?.message || error.message}`, { variant: 'error' });
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const submitData = {
      current_location: formData.current_location,
      status: formData.status,
      available_quantity: formData.available_quantity ? parseFloat(formData.available_quantity) : null,
      reserved_quantity: formData.reserved_quantity ? parseFloat(formData.reserved_quantity) : null,
      remarks: formData.remarks
    };

    updateMutation.mutate({ srr_code: editingItem.srr_code, data: submitData });
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      srr_code: item.srr_code || '',
      current_location: item.current_location || '',
      status: item.status || '',
      available_quantity: item.available_quantity || '',
      reserved_quantity: item.reserved_quantity || '',
      remarks: item.remarks || ''
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
    setFormData({
      srr_code: '',
      current_location: '',
      status: '',
      available_quantity: '',
      reserved_quantity: '',
      remarks: ''
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'available': return 'success';
      case 'reserved': return 'warning';
      case 'issued': return 'info';
      case 'consumed': return 'default';
      default: return 'default';
    }
  };

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Error loading RM status data: {error.message}
        </Alert>
      </Box>
    );
  }

  const rmStatus = rmStatusData?.data?.status || [];
  const pagination = rmStatusData?.data?.pagination || {};
  const summary = rmStatusData?.data?.summary || {};

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Raw Material Status Update
      </Typography>

      {/* Filter Controls */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by SRR code, heat no..."
              InputProps={{
                endAdornment: <SearchIcon />
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Steel Code"
              value={filters.steel_code}
              onChange={(e) => setFilters({ ...filters, steel_code: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Location"
              value={filters.location}
              onChange={(e) => setFilters({ ...filters, location: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="available">Available</MenuItem>
                <MenuItem value="reserved">Reserved</MenuItem>
                <MenuItem value="issued">Issued</MenuItem>
                <MenuItem value="consumed">Consumed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              onClick={() => refetch()}
              startIcon={<RefreshIcon />}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <InventoryIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="h6" color="primary">
              {summary.total_materials?.toLocaleString() || 0}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Total Items
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h6" color="success.main">
              {summary.total_available?.toLocaleString() || 0} kg
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Available Qty
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h6" color="warning.main">
              {summary.total_reserved?.toLocaleString() || 0} kg
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Reserved Qty
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h6" color="info.main">
              {summary.total_quantity_received?.toLocaleString() || 0} kg
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Total Received
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Data Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>SRR Code</TableCell>
              <TableCell>Steel Code</TableCell>
              <TableCell>Heat No</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Available Qty</TableCell>
              <TableCell>Reserved Qty</TableCell>
              <TableCell>Balance</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : rmStatus.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No raw material status records found
                </TableCell>
              </TableRow>
            ) : (
              rmStatus.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>{item.srr_code}</TableCell>
                  <TableCell>{item.steel_code}</TableCell>
                  <TableCell>{item.heat_no}</TableCell>
                  <TableCell>{item.current_location || item.location}</TableCell>
                  <TableCell>
                    <Chip 
                      label={item.status || 'Available'} 
                      color={getStatusColor(item.status)} 
                      size="small" 
                    />
                  </TableCell>
                  <TableCell>{item.available_quantity || item.accepted_quantity}</TableCell>
                  <TableCell>{item.reserved_quantity || 0}</TableCell>
                  <TableCell>
                    {((item.available_quantity || item.accepted_quantity || 0) - (item.reserved_quantity || 0)).toFixed(3)}
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleEdit(item)}>
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={pagination.total || 0}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          showFirstButton
          showLastButton
        />
      </TableContainer>

      {/* Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            Update Raw Material Status
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="SRR Code"
                  value={formData.srr_code}
                  disabled
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Current Location"
                  value={formData.current_location}
                  onChange={(e) => setFormData({ ...formData, current_location: e.target.value })}
                  placeholder="e.g., YARD-A, STORE-1, CUTTING-SECTION"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    label="Status"
                  >
                    <MenuItem value="available">Available</MenuItem>
                    <MenuItem value="reserved">Reserved</MenuItem>
                    <MenuItem value="issued">Issued</MenuItem>
                    <MenuItem value="consumed">Consumed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  step="0.001"
                  label="Available Quantity"
                  value={formData.available_quantity}
                  onChange={(e) => setFormData({ ...formData, available_quantity: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  step="0.001"
                  label="Reserved Quantity"
                  value={formData.reserved_quantity}
                  onChange={(e) => setFormData({ ...formData, reserved_quantity: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Remarks"
                  value={formData.remarks}
                  onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button 
              type="submit" 
              variant="contained"
              disabled={updateMutation.isLoading}
            >
              {updateMutation.isLoading ? 'Updating...' : 'Update Status'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default RMStatusUpdate;